/* eslint-disable no-undef-init */
/* eslint-disable object-shorthand */
import dayjs from 'dayjs';
import React, { createContext, useContext, useState, useMemo } from 'react';
import { removeEmptyValues } from 'src/utils/general';
import * as z from 'zod';
import { t } from 'i18next';
import { PartnerInput } from '../../services/contact';
import { InboundOrder, InboundOrderCreate, InboundOrderUpdate, InboundPackage } from '../../services/inbound';

export const lineSchema = z.object({
  productId: z.number(),
  quantity: z.coerce.number().min(1, t('errors.number_greater_zero_error')),
  labeling: z.enum(['weship', 'customer'] as const).nullable(),
  appliedQualityChecks: z.array(z.number()),
  lotId: z.number().nullable(),
});

export type LineFormValues = z.infer<typeof lineSchema>;

export const packageSchema = z.object({
  name: z.string().nullable().optional(),
  lines: z.array(lineSchema).min(1, t('inbound.one_product_required')),
  packageType: z.enum(['pallet', 'parcel'] as const),
  page: z.number().optional(),
  total_pages: z.number().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  line_count: z.number().optional(),
});

export type PackageFormValues = z.infer<typeof packageSchema>;

export const inboundFormDataStepSchema = z.object({
  name: z
    .string()
    .min(1, t('general.name_required'))
    .superRefine((value, ctx) => {
      if (!/^[a-zA-Z0-9 _-]*$/.test(value ?? '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('errors.umlauts_and_special_characters_error'),
        });
      }
    }),
  datePlanned: z
    .string()
    .refine(date => dayjs(date).isValid(), { message: 'Invalid date format' })
    .refine(
      date => {
        const inputDate = dayjs(date);
        return inputDate.isSame(dayjs(), 'day') || inputDate.isAfter(dayjs(), 'day');
      },
      { message: t('general.date_today_or_future') },
    ),
  reference: z.string().optional().nullable(),
  warehouseId: z.preprocess(
    val => {
      if (val === null || val === undefined) {
        return 'INVALID';
      }
      return val;
    },
    z.union([
      z.coerce.number(),
      z.literal('INVALID').refine(() => false, {
        message: t('inbound.warehouse_required'),
      }),
    ]),
  ),
  partner: z
    .object({
      id: z.number().nullable().optional(),
      name: z.string().nullable().optional(),
      email: z.string().optional().nullable(),
      phone: z.string().optional().nullable(),
      tax_id: z.string().nullable().optional(),
      active: z.boolean().optional(),
      company_name: z.string().min(1, t('general.company_name_required')),
      address: z.object({
        street: z.string().nullable().optional(),
        zip_code: z.string().nullable().optional(),
        city: z.string().nullable().optional(),
        country_code: z.string().nullable().optional(),
        country_name: z.string().nullable().optional(),
      }),
    })
    .passthrough()
    .nullable()
    .optional(),
  packages: z.array(packageSchema).min(1, t('inbound.package_required')),
});

// Inbound-Draft spec 2026-11-03 §6: the terms are asked in the confirm dialog of the
// detail page, the wizard only creates a draft — the checkbox is no longer a gate here.
export const inboundFormSummaryStepSchema = z.object({
  termsAccepted: z.boolean().optional(),
});

export const inboundFormSchema = z.object({
  data: inboundFormDataStepSchema,
  summary: inboundFormSummaryStepSchema,
});

export type InboundFormDataStepValues = z.infer<typeof inboundFormDataStepSchema>;
export type InboundFormSummaryStepValues = z.infer<typeof inboundFormSummaryStepSchema>;
export type InboundFormValues = z.infer<typeof inboundFormSchema>;

export function formValuesToInboundOrder(values: InboundFormValues): InboundOrderCreate {
  if (!values.data.name) throw new Error('Name is required');
  if (!dayjs(values.data.datePlanned).isValid()) throw new Error('Invalid planned date');
  const result: InboundOrderCreate = {
    name: values.data.name,
    date_planned: values.data.datePlanned,
    attachments: null,
    packages: values.data.packages.map(pack => {
      return {
        name: pack.name,
        package_type: pack.packageType,
        lines: pack.lines.map(line => {
          return {
            product_id: line.productId,
            quantity: line.quantity,
            applied_quality_checks: line.appliedQualityChecks,
            lot_id: line.lotId as any,
            labeling: line.labeling as any,
          };
        }),
      };
    }),
    warehouse_id: values.data.warehouseId === 'INVALID' ? null : values.data.warehouseId,
    partner: removeEmptyValues(values.data?.partner as PartnerInput),
  };

  return result;
}

// Inbound-Draft spec 2026-11-03 §3.2/F7: PUT replaces the whole form (header, packages,
// lines); same mapping as the create, minus attachments.
export function formValuesToInboundOrderUpdate(values: InboundFormValues): InboundOrderUpdate {
  const { attachments, ...rest } = formValuesToInboundOrder(values);
  return { ...rest, reference: values.data.reference ?? undefined };
}

export function lineToFormValues(line?: any): LineFormValues {
  const result: LineFormValues = {
    productId: (line?.product.product_id as any) ?? 0,
    quantity: line?.quantity ?? 0,
    labeling: (line?.labeling as any) ?? null,
    appliedQualityChecks: (line?.applied_quality_checks ?? []).map((qc: { id: number }) => qc.id as number),
    lotId: line?.lot?.id ?? null,
  };
  return result;
}

export function packageToFormValues(inboundOrder?: InboundOrder, pack?: InboundPackage): PackageFormValues {
  const result: PackageFormValues = {
    name: pack?.name,
    packageType: (pack?.package_type as any) ?? 'parcel',
    lines: (pack?.lines ?? []).map(lineToFormValues),
    page: inboundOrder?.page ?? 0,
    total_pages: inboundOrder?.total_pages ?? 0,
    limit: inboundOrder?.limit ?? 0,
    offset: inboundOrder?.offset ?? 0,
  };
  return result;
}

export function inboundOrderToFormValues(order?: InboundOrderCreate | InboundOrder): InboundFormValues {
  let warehouseId = null;
  let partner = undefined;
  if (order && 'warehouse' in order) {
    warehouseId = order.warehouse?.id ?? null;
    partner = order.partner || undefined;
  } else if (order && 'warehouse_id' in order) {
    warehouseId = order.warehouse_id;
    partner = order.partner || undefined;
  }

  return {
    data: {
      name: order?.name ?? '',
      datePlanned: order?.date_planned ? order.date_planned : dayjs().toISOString(),
      reference: (order?.reference as string) ?? null,
      warehouseId: warehouseId ?? 'INVALID',
      packages: ((order?.packages as any) ?? []).map(packageToFormValues),
      partner: partner as any,
    },
    summary: {
      termsAccepted: false,
    },
  };
}

interface FormContextType {
  formState: InboundFormValues;
  setFormState: React.Dispatch<React.SetStateAction<InboundFormValues>>;
  resetFormState: () => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

export const InboundFormContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [formState, setFormState] = useState<InboundFormValues>(() => inboundOrderToFormValues());

  const resetFormState = () => {
    const initialState = inboundOrderToFormValues();
    setFormState(initialState);
  };

  const value = useMemo(
    () => ({
      formState,
      setFormState,
      resetFormState,
    }),
    [formState],
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export const useInboundFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useInboundFormContext must be used within an InboundFormContextProvider');
  }
  return context;
};

export const defaultPartner = {
  id: null,
  name: '',
  email: '',
  phone: '',
  tax_id: '',
  active: true,
  company_name: '',
  address: {
    street: '',
    zip_code: '',
    city: '',
    country_code: '',
    country_name: '',
  },
};
